import {plainToInstance} from 'class-transformer';

import {WorkerAPI} from '../api/worker';
import {Socket} from '../classes/socket';
import {EntityArray} from '../core/entityArray';
import {CaseMetadata} from '../entities/caseMetadata';
import {Group, GroupUsersCollection} from '../entities/groups/group';
import {GroupContent} from '../entities/groups/groupContent';
import {GroupRequest} from '../entities/groups/groupRequest';
import {GroupState} from '../entities/groups/groupState';
import {GroupsTag} from '../entities/groups/groupTag';
import {GroupUser} from '../entities/groups/groupUser';
import {Groups} from '../entities/groups/groups';
import {User} from '../entities/user';
import {Helper} from '../helpers/helper';
import {IChatConfig} from '../interfaces/chatConfig.interface';
import {IGroupRequestChangeResponse} from '../interfaces/socket.interface';
import {ChatPermissions} from '../entities/chatPermissions';

/**
 * Main entry point, works as singleton.<br>
 * Contain all method to connect to chat and handle his groups
 *
 * @category Chat
 */
export class LookinChatSDK {

  /** A public static property that contains Chat instance. */
  public static instance: LookinChatSDK = null;

  /**
   * Property that contain chat user, only for internal uses
   *
   * @ignore
   * @internal
   * */
  public static user: User = null;

  /**
   *  Contains all groups of chat
   * */
  public groups: Groups = new Groups();

  /**
   *  Api service to get Worker data from Sakura API
   * */
  public workerApi: WorkerAPI = new WorkerAPI();

  /**
   *  Contains all tags of chat
   * */
  public tags: EntityArray<GroupsTag> = new EntityArray<GroupsTag>();

  /**
   *  Contains all tags of chat
   * */
  public static permissions: ChatPermissions = new ChatPermissions();

  /**
   *  Connection status of chat<br>
   *  Available statuses: {@link ChatConnectionStatus}
   * */
  public connectionStatus: ChatConnectionStatus = 'waiting';

  /**
   * Event that will be called, when groups in this chant get changed<br>
   * {@link registerOnGroupsUpdate}
   *
   * @event
   *
   * @return list of changed group with their change types {@link GroupStateType}
   * */
  private onGroupsUpdate: (type: IGroupRequestChangeResponse[]) => void = null;

  /**
   * Event that will be called, when an event is rejected<br>
   * {@link registerOnRejectUpdate}
   *
   * @event
   *
   * */
  private onRejectUpdate: (errorMessage: string) => void = null;

  /**
   * @ignore
   * @internal
   * */
  private socket: Socket = null;


  /**
   * @ignore
   * @internal
   * */
  public config: IChatConfig = null;

  public connectionAttempt = 0;
  public maxConnectionAttempt = 100;
  public maxConnectionAttemptDelay = 10000;
  public maxConnectionAttemptTimer: number = null;

  /**
   * @ignore
   * @internal
   * */
  private constructor(config: IChatConfig, successCallback: () => void, errorCallback: (err: string) => void) {

    // config.userToken = '123';

    // if it's waiting for next connection attempt
    if (this.maxConnectionAttemptTimer !== null) {
      return;
    }

    this.config = config;

    this.tryConnect(successCallback, errorCallback);
  }


  /**
   * @ignore
   * @internal
   * */
  private tryConnect(successCallback: () => void, errorCallback: (err: string) => void) {
    this.changeConnectionStatus('connecting');

    console.log('trying to connect');

    if (this.connectionAttempt >= this.maxConnectionAttempt) {
      this.changeConnectionStatus('error');
      errorCallback('Ошибка подключения');
    }

    this.connectionAttempt++;

    Socket.connect(this.config).then((socket) => {

      this.connectionAttempt = 0;
      this.socket = socket;

      this.getTags();

      this.changeConnectionStatus('loadingGroups');
      this.getGroups().then(() => {

        this.subscribeToChanges();

        this.changeConnectionStatus('ready');
        successCallback();
      }).catch((error) => {
        errorCallback(error.message)
      });

    }).catch((error) => {

      if (error.tryReconnect) {
        if (this.maxConnectionAttemptTimer) {
          clearTimeout(this.maxConnectionAttemptTimer);
          this.maxConnectionAttemptTimer = null;
        }

        this.maxConnectionAttemptTimer = setTimeout(() => {
          this.tryConnect(successCallback, errorCallback);

          clearTimeout(this.maxConnectionAttemptTimer);
          this.maxConnectionAttemptTimer = null;

        }, this.maxConnectionAttemptDelay)
      } else {

        this.changeConnectionStatus('error');
        errorCallback(error.message);
      }
    })
  }

  /**
   * Will create connection, if i`ts not connected yet, and returns Chat singleton instance
   *
   * @param user - User class that contain worker and user id
   * @param config - Chat configuration
   * @param permissions - List of user permission, must be reworked
   *
   * @example
   * LookinChatSDK.connect(this.user, {
   *   socketUrl: 'http://localhost:3000',
   *   uploadUrl: 'http://localhost:3000/upload',
   *   userToken: this.token,
   * }).then(async (chat) => {
   *   this.chat = chat;
   * });
   *
   * @return Promise with chat instance, or will throw error
   * */
  public static connect(user: User, config: IChatConfig, permissions: string[] = []): Promise<LookinChatSDK> {
    return new Promise<LookinChatSDK>((resolve, reject) => {

      // @todo change permissions so it must be retrieved directly from server
      LookinChatSDK.permissions = new ChatPermissions(...permissions);

      LookinChatSDK.user = new User(user.id, user.workerId);

      if (!LookinChatSDK.instance) {
        const tempInstance = new LookinChatSDK(config, () => {
          LookinChatSDK.instance = tempInstance;
          resolve(LookinChatSDK.instance);
        }, (error) => {
          reject(error);
        });
      } else {
        resolve(LookinChatSDK.instance);
      }
    });
  }

  /**
   * Disconnect from socket and remove chat and sockets singleton
   * */
  public static disconnect() {
    if (LookinChatSDK.instance) {
      LookinChatSDK.instance.socket.disconnect();

      LookinChatSDK.instance.groups = new Groups();
      LookinChatSDK.user = null;

      LookinChatSDK.instance = null;
    }
  }

  /**
   * Will register anonymous function to change event.
   *
   * @param callback - Anonymous function that will be called each time when groups in this chat get change.
   * */
  public registerOnGroupsUpdate(callback: (items: IGroupRequestChangeResponse[]) => void) {
    this.onGroupsUpdate = callback;
  }

  /**
   * Will register anonymous function to change event.
   *
   * @param callback - Anonymous function that will be called each time when groups in this chat get change.
   * */
  public registerOnRejectUpdate(callback: (errorMessage: string) => void) {
    this.onRejectUpdate = callback;
  }

  /**
   * Get all groups of chat<br>
   * This methods will be called after connection will be created, as part of initial loading<br>
   * Results will be added to {@link groups}<br>
   * <br>
   * In future will be used with pagination and kind of filters
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  public getGroups() {
    return new Promise<void>((resolve, reject) => {
      this.groups = new Groups();

      this.socket.groupSocket.getGroups().then((results) => {
        this.groups.push(...results.items)
        resolve();
      }).catch((error) => {

        console.log(error);

        reject(error)
      });
    });
  }

  /**
   * Get all tags for this chat<br>
   * This methods will be called after connection will be created, as part of initial loading<br>
   * Also this method called when groups are changed<br>
   * Results will be added to {@link tags}<br>
   * <br>
   * In future will be used with pagination and kind of filters
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  public getTags() {
    return new Promise<void>((resolve, reject) => {
      this.socket.groupSocket.getTags().then((tags) => {
        this.tags = new EntityArray<GroupsTag>();
        this.tags.push(...tags)
        resolve();
      }).catch((error) => {

        console.log(error);

        reject(error)
      });
    });
  }

  /**
   * Create group from case class<br>
   * For general process of creation group see {@link createGroup}
   *
   * @param caseMetadata - class
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  public createGroupFromCase(caseMetadata: CaseMetadata) {
    return this.createGroup(null, [], caseMetadata);
  }

  /**
   * Create group from case, but from object<br>
   * For general process of creation group see {@link createGroup}
   *
   * @param caseMetadata - partial object, class will be created
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  public createGroupFromCaseObject(caseMetadata: Partial<CaseMetadata>) {
    return this.createGroupFromCase(plainToInstance(CaseMetadata, caseMetadata));
  }

  /**
   * Create group from contant and user data<br>
   * For general process of creation group see {@link createGroup}
   *
   * @param content - partial object, Group content
   * @param users - list of users with their data
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  public createGroupFromData(content: Partial<GroupContent>, users: GroupUser[]) {
    return this.createGroup(content, users);
  }

  /**
   * Should not be called outside
   *
   * @ignore
   * @internal
   * */
  public callOnGroupsUpdate(items: IGroupRequestChangeResponse[] = []) {
    if (this.onGroupsUpdate) {
      this.onGroupsUpdate(items);
    }
  }

  /**
   * Should not be called outside
   *
   * @ignore
   * @internal
   * */
  public callOnRejectUpdate(errorMessage: string) {
    if (this.onRejectUpdate && errorMessage) {
      this.onRejectUpdate(errorMessage);
    }
  }

  /**
   * Private method that called when {@link createGroupFromCase} or {@link createGroupFromCaseTest} called<br>
   * <br>
   * Will create group change request with `create` type {@link GroupStateType}<br>
   * In this case new group with status `sending` {@link GroupStatus} will be added to {@link groups}, {@link onGroupsUpdate} will be called<br>
   * After server will save it, new group will be updated in {@link groups}, {@link onGroupsUpdate} will be called again, also with {@link Group.onGroupUpdate}
   *
   * @return Promise, resolve if all ok, throw error in case of error
   * */
  private createGroup(content: Partial<GroupContent>, users: GroupUser[] = [], caseMetadata: CaseMetadata = null) {
    return new Promise<Group>((resolve, reject) => {

      if (content?.logo) {
        content.logo = Helper.convertFile(content.logo);
      }

      // @todo refactor, lot of blocks like this, must be changed
      const newState = new GroupState('create');
      const newContent = content ? plainToInstance(GroupContent, content) : new GroupContent();
      const request = new GroupRequest(['create'], newState);

      const group = new Group(newContent, newState, request.clientId);

      request.data.content = newContent;

      if (users) {
        request.data.users = users;
        group.users = new GroupUsersCollection();
        group.users.push(...users);
      }

      if (caseMetadata) {
        request.caseMetadata = caseMetadata;
        group.caseMetadata = caseMetadata;
        group.type = caseMetadata.getGroupType()
      }

      // this.groups.push(group);
      group.callOnGroupUpdate(['create']);
      console.log({group});
      // @todo error handle, if group wasnt saved

      Socket.instance.groupSocket.sendRequest(request).then((item) => {
        this.processGroupRequestEvent([item], true);
        resolve(item.group)
      }).catch((error) => {
        LookinChatSDK.instance.callOnRejectUpdate(error.message);
        reject(error);
      });
    });
  }

  /**
   * Available reactions
   *
   * @return array of reactions
   * */
  public static availableReactions() {
    // @todo get from server

    return ['👍', '👎', '😭', '😱', '❤️', '😂']
  }

  /**
   * Only for internal use, d`ont call from outside
   *
   * @ignore
   * @internal
   * */
  public processGroupRequestEvent(items: IGroupRequestChangeResponse[], useClientId = false) {

    items.forEach((item) => {
      // @todo maybe we can solve with getter of id or clientId ?
      const group = this.groups.getByField(useClientId ? 'clientId' : 'id', useClientId ? item.group.clientId : item.group.id);

      if (group) {
        group.update(item.group, item.type);

        if (item.type.includes('delete')) {
          this.groups.removeById(item.group.id)
        }

      // LSA-59: при удалении чата функция processGroupRequestEvent помещала
      // айдишник удаленного чата обратно в список, чего быть не должно.
      } else if (!item.type.includes('delete')) {
        this.groups.push(plainToInstance(Group, item.group))
      }
    });

    this.getTags().then(_ => this.callOnGroupsUpdate(items));
  }

  /**
   * @ignore
   * @internal
   * */
  private subscribeToChanges() {
    Socket.instance.groupSocket.subscribeToChanges((items) => {
      this.processGroupRequestEvent(items);
    })
  }

  /**
   * @ignore
   * @internal
   * */
  private changeConnectionStatus(newStatus: ChatConnectionStatus) {
    this.connectionStatus = newStatus;
    // @todo add callback if needed
  }
}

/**
 * Chat connection Statuses
 * */
export type ChatConnectionStatus =
  'waiting'
  | 'connecting'
  | 'loadingGroups'
  | 'ready'
  | 'error';
